import React from 'react';
import { Box, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import { PRES } from '../chrome';

export const toolbarToggleSx = {
  '& .MuiToggleButton-root': {
    borderColor: PRES.borderSubtle,
    color: PRES.textPrimary,
    px: 0.75,
    minWidth: 34,
    minHeight: 32,
  },
  '& .Mui-selected': {
    bgcolor: `${PRES.surfaceSunken} !important`,
    color: `${PRES.interactive} !important`,
  },
};

export const SolidLineIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: 2,
      bgcolor: 'currentColor',
      display: 'inline-block',
      borderRadius: 1,
    }}
  />
);

export const DottedLineIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box
    component="span"
    sx={{
      width: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    {Array.from({ length: 5 }).map((_, index) => (
      <Box
        key={index}
        sx={{ width: 3.5, height: 3.5, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }}
      />
    ))}
  </Box>
);

export const DashedLineIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: 0,
      borderTop: '2px dashed currentColor',
      display: 'inline-block',
    }}
  />
);

export const LineDashToggle: React.FC<{
  value: 'solid' | 'dashed' | 'dotted';
  disabled?: boolean;
  onChange: (value: 'solid' | 'dashed' | 'dotted') => void;
}> = ({ value, disabled, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    disabled={disabled}
    value={value}
    onChange={(_event, next) => next && onChange(next)}
    sx={toolbarToggleSx}
  >
    {([
      { id: 'solid' as const, title: 'Heldragen', icon: <SolidLineIcon size={22} /> },
      { id: 'dashed' as const, title: 'Streckad', icon: <DashedLineIcon size={22} /> },
      { id: 'dotted' as const, title: 'Prickad', icon: <DottedLineIcon size={22} /> },
    ]).map((item) => (
        <ToggleButton key={item.id} value={item.id} title={item.title} aria-label={item.title}>
          {item.icon}
        </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

export const TextAlignToggle: React.FC<{
  value: 'left' | 'center' | 'right';
  disabled?: boolean;
  onChange: (value: 'left' | 'center' | 'right') => void;
}> = ({ value, disabled, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    disabled={disabled}
    value={value}
    onChange={(_event, next) => next && onChange(next)}
    sx={toolbarToggleSx}
  >
    <ToggleButton value="left" title="Vänster"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
    <ToggleButton value="center" title="Mitten"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
    <ToggleButton value="right" title="Höger"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
  </ToggleButtonGroup>
);

export const VerticalAlignToggle: React.FC<{
  value: 'top' | 'middle' | 'bottom';
  disabled?: boolean;
  onChange: (value: 'top' | 'middle' | 'bottom') => void;
}> = ({ value, disabled, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    disabled={disabled}
    value={value}
    onChange={(_event, next) => next && onChange(next)}
    sx={toolbarToggleSx}
  >
    <ToggleButton value="top" title="Topp"><VerticalAlignTopIcon fontSize="small" /></ToggleButton>
    <ToggleButton value="middle" title="Mitten"><VerticalAlignCenterIcon fontSize="small" /></ToggleButton>
    <ToggleButton value="bottom" title="Botten"><VerticalAlignBottomIcon fontSize="small" /></ToggleButton>
  </ToggleButtonGroup>
);

export const InsertButton: React.FC<{
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, active, onClick, children }) => (
  <Tooltip title={label} arrow enterDelay={350}>
    <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-pressed={active}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          minWidth: 32,
          height: 32,
          p: 0,
          border: `1px solid ${active ? PRES.borderSubtle : 'transparent'}`,
          bgcolor: active ? PRES.surfaceSunken : 'transparent',
          color: active ? PRES.interactive : PRES.textPrimary,
          cursor: 'pointer',
          borderRadius: 1,
          '&::after': active ? {
            content: '""',
            position: 'absolute',
            left: 7,
            right: 7,
            bottom: 2,
            height: 2,
            borderRadius: 99,
            bgcolor: PRES.interactive,
          } : undefined,
          '&:hover': { bgcolor: PRES.surfaceSunken, color: PRES.interactive },
          '&:focus': { outline: 'none' },
          '&:focus-visible': { boxShadow: `0 0 0 2px ${PRES.surface}, 0 0 0 4px ${PRES.focusRing}` },
        }}
      >
        {children}
      </Box>
    </Box>
  </Tooltip>
);

const insertIconSx = { display: 'block' };

export const InsertTextIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={insertIconSx} aria-hidden>
    <path d="M4 4h12M10 4v12M7 16h6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </Box>
);

export const InsertImageIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={insertIconSx} aria-hidden>
    <rect x="2.75" y="3.25" width="14.5" height="13.5" rx="1.75" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="7" cy="7.25" r="1.35" fill="none" stroke="currentColor" strokeWidth="1.35" />
    <path d="m4.25 14 3.5-3.5 2.25 2 2.25-2.5 3.5 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

export const InsertFileIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={insertIconSx} aria-hidden>
    <path d="M5 2.75h6l4 4V17.25H5zM11 2.75v4h4M10 15V9M7.75 11.25 10 9l2.25 2.25" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

export const InsertProductIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={insertIconSx} aria-hidden>
    <path d="m3.5 6.25 6.5-3.5 6.5 3.5v7.5L10 17.25l-6.5-3.5zM3.5 6.25 10 9.8l6.5-3.55M10 9.8v7.45M7 4.35l6.5 3.55" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

export const InsertSymbolIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={insertIconSx} aria-hidden>
    <circle cx="5.25" cy="6" r="2.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <rect x="11.75" y="3.75" width="4.5" height="4.5" rx="0.75" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="m5.25 11.5 2.75 4.75H2.5zM13.9 11.5l2.6 4.75h-5.2z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </Box>
);

export const InsertShapeIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={insertIconSx} aria-hidden>
    <rect x="2.75" y="3.5" width="7" height="7" rx="1.1" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="13.25" cy="12.5" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </Box>
);

export const ShapeRectangleIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={insertIconSx} aria-hidden>
    <rect x="3" y="6" width="16" height="10" rx="0.75" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </Box>
);

export const ShapeRoundedRectangleIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={insertIconSx} aria-hidden>
    <rect x="3" y="6" width="16" height="10" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </Box>
);

export const ShapeEllipseIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={insertIconSx} aria-hidden>
    <ellipse cx="11" cy="11" rx="7.5" ry="7.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </Box>
);

export const ShapeTriangleIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={insertIconSx} aria-hidden>
    <path d="M11 3.5 19 18H3z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </Box>
);

export const ToolbarDivider: React.FC = () => (
  <Box sx={{ width: '1px', height: 28, bgcolor: PRES.line, mx: 0.75, flexShrink: 0 }} />
);

export const ToolbarIconButton: React.FC<{
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  disabled?: boolean;
  selected?: boolean;
}> = ({ label, onClick, children, disabled, selected }) => (
  <Tooltip title={label} arrow>
    <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={label}
        aria-pressed={selected}
        onClick={onClick}
        sx={{
          width: 32,
          height: 32,
          p: 0,
          borderRadius: 1,
          color: selected ? PRES.interactive : PRES.textPrimary,
          bgcolor: selected ? PRES.surfaceSunken : 'transparent',
          border: `1px solid ${selected ? PRES.interactive : 'transparent'}`,
          '&:hover': { bgcolor: PRES.surfaceSunken, borderColor: PRES.borderSubtle },
          '&:focus-visible': { boxShadow: `0 0 0 2px ${PRES.surface}, 0 0 0 4px ${PRES.focusRing}` },
        }}
      >
        {children}
      </IconButton>
    </Box>
  </Tooltip>
);

const chartIconSx = { display: 'block' };

export const ChartColumnIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={chartIconSx} aria-hidden>
    <rect x="3" y="11" width="4" height="8" rx="1" fill="currentColor" />
    <rect x="9" y="6" width="4" height="13" rx="1" fill="currentColor" />
    <rect x="15" y="9" width="4" height="10" rx="1" fill="currentColor" />
  </Box>
);

export const ChartBarIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={chartIconSx} aria-hidden>
    <rect x="3" y="4" width="10" height="3.5" rx="1" fill="currentColor" />
    <rect x="3" y="9.25" width="16" height="3.5" rx="1" fill="currentColor" />
    <rect x="3" y="14.5" width="13" height="3.5" rx="1" fill="currentColor" />
  </Box>
);

export const ChartLineIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={chartIconSx} aria-hidden>
    <path d="M3 16 L8 10 L13 13 L19 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="3" cy="16" r="1.6" fill="currentColor" />
    <circle cx="8" cy="10" r="1.6" fill="currentColor" />
    <circle cx="13" cy="13" r="1.6" fill="currentColor" />
    <circle cx="19" cy="5" r="1.6" fill="currentColor" />
  </Box>
);

export const ChartPieIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 22 22" sx={chartIconSx} aria-hidden>
    <path d="M11 3 A8 8 0 1 1 3 11 H11 Z" fill="currentColor" opacity="0.35" />
    <path d="M11 3 A8 8 0 0 1 19 11 H11 Z" fill="currentColor" />
  </Box>
);

export const ChartLegendIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={chartIconSx} aria-hidden>
    <rect x="2" y="4" width="4" height="4" rx="1" fill="currentColor" />
    <rect x="8" y="5" width="10" height="2" rx="1" fill="currentColor" />
    <rect x="2" y="12" width="4" height="4" rx="1" fill="currentColor" opacity="0.45" />
    <rect x="8" y="13" width="10" height="2" rx="1" fill="currentColor" />
  </Box>
);

export const ChartValuesIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={chartIconSx} aria-hidden>
    <rect x="3" y="11" width="3.2" height="6" rx="0.8" fill="currentColor" />
    <rect x="8.4" y="6" width="3.2" height="11" rx="0.8" fill="currentColor" />
    <rect x="13.8" y="8.5" width="3.2" height="8.5" rx="0.8" fill="currentColor" />
    <text x="4.6" y="9" textAnchor="middle" fontSize="5" fill="currentColor">4</text>
    <text x="10" y="5" textAnchor="middle" fontSize="5" fill="currentColor">7</text>
  </Box>
);

export const TableHeaderIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={chartIconSx} aria-hidden>
    <rect x="2.5" y="3" width="15" height="14" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.25 7.25h13.5M7.5 3.75v12.5M12.5 3.75v12.5" fill="none" stroke="currentColor" strokeWidth="1.25" />
    <rect x="3.25" y="3.75" width="13.5" height="3.5" fill="currentColor" opacity="0.28" />
  </Box>
);

export const TableAddRowIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={chartIconSx} aria-hidden>
    <rect x="2.5" y="3" width="11" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.25 7h9.5M3.25 10.5h9.5M6.2 3.75v9.5M9.8 3.75v9.5M14.5 15.5h3M16 14v3" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
  </Box>
);

export const TableRemoveRowIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={chartIconSx} aria-hidden>
    <rect x="2.5" y="3" width="11" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.25 7h9.5M3.25 10.5h9.5M6.2 3.75v9.5M9.8 3.75v9.5M14.5 15.5h3" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
  </Box>
);

export const TableAddColumnIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={chartIconSx} aria-hidden>
    <rect x="2.5" y="3" width="11" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.25 7h9.5M3.25 10.5h9.5M6.2 3.75v9.5M9.8 3.75v9.5M16 12.5v5M13.5 15h5" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
  </Box>
);

export const TableRemoveColumnIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box component="svg" width={size} height={size} viewBox="0 0 20 20" sx={chartIconSx} aria-hidden>
    <rect x="2.5" y="3" width="11" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.25 7h9.5M3.25 10.5h9.5M6.2 3.75v9.5M9.8 3.75v9.5M13.5 15h5" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
  </Box>
);
